import { useEffect, useState, forwardRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, ChevronDown, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Logo } from "@/components/site/Logo";
import { SoundToggle } from "@/components/site/SoundToggle";
import { BookingDialog } from "@/components/site/BookingDialog";
import { NAV_LINKS } from "@/lib/content";
import { cn } from "@/lib/utils";

const CtaButton = forwardRef(({ className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    data-testid="global-nav-book-briefing-button"
    className={cn(
      "group inline-flex items-center gap-2 rounded-full bg-[color:var(--bp-teal)] px-5 py-2.5 text-[13px] font-semibold text-[color:var(--bp-bg)] shadow-[var(--shadow-soft)] transition-shadow hover:shadow-[var(--shadow-lift)]",
      className
    )}
    {...props}
  >
    Book a Briefing
    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
  </button>
));
CtaButton.displayName = "CtaButton";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 36);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      data-testid="global-nav"
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-300",
        scrolled
          ? "border-b border-[color:var(--bp-border)]/70 bg-[color:rgba(8,24,48,0.82)] backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      )}
    >
      <nav className="container-bp flex items-center justify-between py-3.5">
        <Logo />

        <div className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((link) =>
            link.children ? (
              <div key={link.label} className="group relative">
                <Link
                  to={link.to}
                  className="inline-flex items-center gap-1 rounded-full px-3.5 py-2 text-[13px] font-medium text-[color:var(--bp-text-muted)] transition-colors hover:text-[color:var(--bp-cream)]"
                >
                  {link.label}
                  <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
                </Link>
                <div className="invisible absolute left-0 top-full w-64 translate-y-2 pt-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                  <div className="overflow-hidden rounded-[var(--radius-md)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] p-1.5 shadow-[var(--shadow-lift)]">
                    {link.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        className="block rounded-[var(--radius-sm)] px-3.5 py-2.5 text-[13px] text-[color:var(--bp-text-muted)] transition-colors hover:bg-[color:var(--bp-bg-2)] hover:text-[color:var(--bp-teal)]"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={link.to}
                to={link.to}
                className={cn(
                  "rounded-full px-3.5 py-2 text-[13px] font-medium transition-colors hover:text-[color:var(--bp-cream)]",
                  location.pathname === link.to ? "text-[color:var(--bp-teal)]" : "text-[color:var(--bp-text-muted)]"
                )}
              >
                {link.label}
              </Link>
            )
          )}
        </div>

        <div className="flex items-center gap-2.5">
          <SoundToggle className="hidden sm:inline-flex" />
          <div className="hidden lg:block">
            <BookingDialog trigger={<CtaButton />} />
          </div>

          {/* Mobile */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                data-testid="global-nav-mobile-menu-button"
                aria-label="Open menu"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--bp-border)] text-[color:var(--bp-cream)] lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] max-w-sm overflow-y-auto border-[color:var(--bp-border)] bg-[color:var(--bp-bg)] p-0">
              <div className="flex items-center justify-between border-b border-[color:var(--bp-border)] px-5 py-4">
                <Logo />
              </div>
              <div className="px-5 py-6">
                <div className="flex flex-col">
                  {NAV_LINKS.map((link) => (
                    <div key={link.label} className="border-b border-[color:var(--bp-border)]/50">
                      <SheetClose asChild>
                        <Link to={link.to} className="block py-3.5 font-display text-lg text-[color:var(--bp-cream)]">
                          {link.label}
                        </Link>
                      </SheetClose>
                      {link.children && (
                        <div className="-mt-1 mb-3 flex flex-col gap-1 pl-3">
                          {link.children.map((c) => (
                            <SheetClose asChild key={c.to}>
                              <Link to={c.to} className="py-1.5 text-[13px] text-[color:var(--bp-text-muted)]">
                                {c.label}
                              </Link>
                            </SheetClose>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  <SheetClose asChild>
                    <Link to="/contact" className="block py-3.5 font-display text-lg text-[color:var(--bp-cream)]">Contact</Link>
                  </SheetClose>
                </div>
                <div className="mt-6 flex items-center justify-between">
                  <SoundToggle withLabel />
                </div>
                <div className="mt-5" onClick={() => setMobileOpen(false)}>
                  <BookingDialog trigger={<CtaButton className="w-full justify-center py-3" />} />
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </motion.header>
  );
};
